import {
  fillArrayWithNumbers,
  isGymMultiAreaGym, ProcessTypes, UserProfile,
} from 'fitbeat.gym-core';
import { IDay, TimeSpan } from 'fitbeat.models';
import React, { FunctionComponent, useState } from 'react';
import { translate } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import DatePicker from 'react-native-date-picker';
import { connect } from 'react-redux';

import { Button, ButtonSize, ButtonTypes } from '../../components';
import { IAppStore } from '../../HomeWorkouts';
import { GymResolver } from '../../services';
import Theme from '../../theme';
import { FormError } from '../../ui';
import { addDaysToEndOfToday, i18n } from '../../utils';
import { defaultScale } from '../../utils/scaling';
import { isWeb } from '../../utils/ScreenSizes';
import {
  clearLoadedSlots,
  getNextBookingStep as dispatchGetNextBookingStep,
  getPreviousBookingStep as dispatchGetPreviousBookingStep,
  workoutDateTimeSelected as dispatchWorkoutDateTimeSelected,
} from '../actions';
import {
  BookingFlowFooter, BookingInstructionHeader,
  HomeTimeSlotSelection,
} from '../components';
import { styles as sharedStyles } from './Styles';

export interface IProps {
  day: IDay;
  userProfile: UserProfile;
  getNextBookingStep: (userProfile: UserProfile, processType?: ProcessTypes) => void;
  getPreviousBookingStep: () => void;
  currentSelectedGymName: string | null;
  bookingSlotsError: string | null;
  bookingProcess: ProcessTypes;
  fromPlanner: boolean;
  t(key: string): string;
  workoutDateTimeSelected(date: Date): void;
}

const SelectPreferredTime: FunctionComponent<IProps> = ({
  t,
  day,
  userProfile,
  getNextBookingStep,
  getPreviousBookingStep,
  bookingSlotsError,
  workoutDateTimeSelected,
  bookingProcess,
  currentSelectedGymName,
  fromPlanner,
}) => {
  const [date, setDate] = useState(new Date(day.date));

  const nextClicked = () => {
    getNextBookingStep(userProfile);
  };

  if (!currentSelectedGymName) {
    return <></>;
  }

  const gym = GymResolver.getByName(currentSelectedGymName);
  const isMultiAreaGym = isGymMultiAreaGym(gym);

  const isDemoStudioBooking = bookingProcess === ProcessTypes.DemoStudioBooking;
  const BookingInstructionHeading = !isDemoStudioBooking
    ? isMultiAreaGym ? t('choosePreferredTime') : t('chooseStartDate')
    : t('bookingDaySelectHeaderForDemo');

  const intervals = fillArrayWithNumbers(60, { startingNumber: 0 });

  const getDateTimePickerMode = () => {
    if (fromPlanner && isMultiAreaGym) {
      return 'time';
    }

    if (!fromPlanner && !isMultiAreaGym) {
      return 'date';
    }

    return 'datetime';
  };

  const maximumDays = 14;

  return (
    <View style={styles.container}>
      <View style={sharedStyles.body}>
        <BookingInstructionHeader header={BookingInstructionHeading} />
        {isWeb() ? (
          <HomeTimeSlotSelection
            t={t}
            useRadiosSelections={false}
            day={day}
            userProfile={userProfile}
            workoutDateTimeSelected={workoutDateTimeSelected}
            containerStyle={{ paddingVertical: 0 }}
            minutesInterval={intervals}
            mode={getDateTimePickerMode()}
          />
        ) : (
          <DatePicker
            date={date}
            onDateChange={(datetime) => {
              const formattedDate = isMultiAreaGym ? datetime : TimeSpan.createStartOfDayDate(datetime);
              setDate(formattedDate);
              workoutDateTimeSelected(formattedDate);
            }}
            mode={getDateTimePickerMode()}
            androidVariant='nativeAndroid'
            minimumDate={new Date()}
            maximumDate={addDaysToEndOfToday(maximumDays - 1)}
          />
        )}
      </View>
      <BookingFlowFooter>
        {bookingSlotsError && (
          <FormError error={new Error(bookingSlotsError)} />
        )}
        <View style={styles.footer}>
          {!isDemoStudioBooking && (
            <Button
              fontColor={Theme.PRIMARY}
              size={ButtonSize.MEDIUM}
              buttonType={ButtonTypes.OUTLINE}
              title={i18n.t('bookings:back')}
              onPress={() => getPreviousBookingStep()}
              containerStyle={styles.backContainer}
            />
          )}
          <Button
            size={ButtonSize.MEDIUM}
            fontColor={Theme.WHITE}
            title={i18n.t('bookings:next')}
            onPress={nextClicked}
            containerStyle={[
              styles.nextContainer,
              isDemoStudioBooking && { flex: 1 },
            ]}
          />
        </View>
      </BookingFlowFooter>
    </View>
  );
};

const mapStateToProps = (state: IAppStore): Partial<IProps> => ({
  bookingProcess: state.bookings.bookingWizard.processType,
  currentSelectedGymName: state.bookings.bookingWizard.currentSelectedGymName,
  fromPlanner: state.bookings.bookingWizard.fromPlanner,
});

const mapDispatchToProps = (dispatch) => ({
  getNextBookingStep: (userProfile, processType) => {
    dispatch(clearLoadedSlots());
    dispatch(dispatchGetNextBookingStep(userProfile, processType));
  },
  getPreviousBookingStep: () => dispatch(dispatchGetPreviousBookingStep()),
  workoutDateTimeSelected: (date) => dispatch(dispatchWorkoutDateTimeSelected(date)),
});

export const SelectPreferredTimeStep = connect(mapStateToProps, mapDispatchToProps)(
  translate(['bookings', 'common'], { wait: true })(SelectPreferredTime),
);

SelectPreferredTimeStep.getTitle = () => <></>;

const styles = StyleSheet.create({
  scrollViewContainer: {
    paddingBottom: defaultScale(60),
  },
  container: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
  },
  dateTilesContainer: {
    alignItems: 'center',
  },
  studioHeader: {
    paddingTop: defaultScale(15),
    paddingBottom: 0,
  },
  footer: {
    flexDirection: 'row',
  },
  backContainer: {
    flex: 0.3,
  },
  nextContainer: {
    flex: 0.7,
    marginLeft: defaultScale(5),
  },
});
